/** Operations - *what is moving and what is stuck* (ADR-0195, the third fixed view).
 *
 * For the person running the close: the work queue (ADR-0164, derived on read), the approvals
 * waiting, the period's lock state, and the unattended work the server reports on itself (OPS-01).
 * The documents' stages are on Data health, where the evidence is; they are not repeated here, so
 * two tiles cannot count one thing twice and disagree.
 *
 * **A queue the server could not read is not an empty queue.** A feed with a `note` says which kind
 * of item it could not read, and the tile prints it; "nothing outstanding" is only said for a read
 * that succeeded without one.
 */

import { Link } from "@tanstack/react-router";

import { mayHaveQueue, useAwaitingMe } from "../../api/approvals";
import { useMe } from "../../api/auth";
import { useWorkItems } from "../../api/homeSummary";
import { useLiveness } from "../../api/ops";
import { useInventory } from "../../api/queries";
import { StatusChip } from "../../components/ui/StatusChip";
import { quantity, shortDate } from "../../lib/format";
import { actionFor } from "../homeWords";
import type { Screen } from "../useScreen";
import styles from "./Dashboards.module.css";
import { carryOf } from "./dashboardModel";
import { BarRows } from "./parts";
import type { BarRow } from "./parts";
import { Tile } from "./Tile";

const LOCK_WORDS: Record<string, string> = {
  open: "Open: figures can still change",
  closed: "Closed: this period is locked",
  includes_closed: "Reaches a closed period",
};

export function OperationsView({ screen }: { screen: Screen }) {
  const carry = carryOf(screen);
  const meRead = useMe();
  const me = meRead.data;
  const items = useWorkItems({ limit: 50 });
  const inventory = useInventory(screen.window, screen.method);
  const queue = useAwaitingMe(mayHaveQueue(me?.role));
  const liveness = useLiveness(me?.role === "admin");

  const counts = items.data?.counts;
  const scopes = counts
    ? Object.entries(counts.by_scope as Record<string, number>).sort(([a], [b]) => a.localeCompare(b))
    : [];
  const biggest = Math.max(0, ...scopes.map(([, count]) => count));
  const overdue = (items.data?.items ?? []).filter((item) => item.overdue);

  return (
    <div className={styles.view} data-testid="dashboard-operations">
      <div className={styles.span5}>
        <Tile
          id="ops-queue"
          title="The work queue"
          sub="Open items by scope, derived from the data"
          of="The work queue"
          reads={[items]}
          action={
            <Link to="/tasks" search={carry} className={styles.link}>
              Tasks &amp; requests
            </Link>
          }
        >
          {counts ? (
            <>
              <div className={styles.tally} data-testid="dash-queue-tally">
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(counts.total))}</strong>
                  <span>open</span>
                </div>
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(overdue.length))}</strong>
                  <span>overdue</span>
                </div>
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(counts.blocking))}</strong>
                  <span>holding a filing</span>
                </div>
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(counts.nobody_named))}</strong>
                  <span>nobody named</span>
                </div>
              </div>
              {scopes.length > 0 ? (
                <BarRows
                  testId="dash-queue-scopes"
                  caption="Open items by scope"
                  head={["Scope", "Items"]}
                  rows={scopes.map(
                    ([scope, count]): BarRow => ({
                      id: scope,
                      name: scope === "1" || scope === "2" || scope === "3" ? `Scope ${scope}` : scope.replace(/_/g, " "),
                      fraction: biggest > 0 ? count / biggest : 0,
                      figure: String(count),
                      fill: scope === "1" ? "scope1" : scope === "2" ? "scope2" : "info",
                      to: { to: "/tasks", search: carry },
                    }),
                  )}
                />
              ) : (
                <p className={styles.empty}>Nothing is open.</p>
              )}
              {items.data?.notes.length ? (
                <p className={styles.note} role="status" data-testid="dash-queue-notes">
                  {items.data.notes.length === 1 ? "One kind of item" : `${items.data.notes.length} kinds of item`} could
                  not be read, so this is not the whole queue. The Tasks &amp; requests list says which.
                </p>
              ) : null}
            </>
          ) : null}
        </Tile>
      </div>

      <div className={styles.span7}>
        <Tile
          id="ops-overdue"
          title="What is overdue"
          sub="Each opens the place it is fixed"
          of="The work queue"
          reads={[items]}
        >
          {overdue.length === 0 ? (
            <p className={styles.empty}>No open item is past its date.</p>
          ) : (
            <ul className={styles.rows} data-testid="dash-overdue">
              {overdue.slice(0, 8).map((item) => {
                const action = actionFor(item);
                return (
                  <li key={item.item_key}>
                    <Link
                      to={action.to}
                      search={carry}
                      className={`${styles.row} ${styles.rowLink}`}
                      aria-label={`${item.title}. ${action.label}.`}
                    >
                      <span className={styles.name}>
                        {item.title}
                        <span className={styles.sub}>
                          {item.facility_name ? `${item.facility_name}, ` : ""}
                          due {shortDate(item.due)}
                        </span>
                      </span>
                      <span>
                        {item.blocks.length > 0 ? (
                          <StatusChip tone="warn" size="sm">
                            Holds a filing
                          </StatusChip>
                        ) : null}
                      </span>
                      <span className={styles.words}>{action.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Tile>
      </div>

      <div className={styles.span4}>
        <Tile
          id="ops-approvals"
          title="Approvals waiting"
          sub="On you, in the order they arrived"
          of="Your approval queue"
          reads={mayHaveQueue(me?.role) ? [meRead, queue] : [meRead]}
          action={
            <Link to="/approvals" search={carry} className={styles.link}>
              Approvals
            </Link>
          }
        >
          {!mayHaveQueue(me?.role) ? (
            <p className={styles.empty} data-testid="dash-no-queue">
              Your role has no approval queue, so this is not a count of zero. Approvers and Admins decide requests.
            </p>
          ) : (
            <p className={styles.big} data-testid="dash-approvals-count">
              {queue.data?.length ?? 0}
              <span className={styles.bigUnit}>{queue.data?.length === 1 ? "request" : "requests"} waiting</span>
            </p>
          )}
        </Tile>
      </div>

      <div className={styles.span4}>
        <Tile
          id="ops-lock"
          title="The period"
          sub="Whether its figures can still change"
          of="The period's lock state"
          reads={[inventory]}
        >
          {inventory.data ? (
            <>
              <p className={styles.big} data-state={inventory.data.window_lock.state} data-testid="dash-lock">
                {inventory.data.window_lock.state === "open" ? "Open" : "Closed"}
              </p>
              <p className={styles.note}>
                {LOCK_WORDS[inventory.data.window_lock.state] ?? inventory.data.window_lock.state}
                {inventory.data.window_lock.closed.length > 0
                  ? `: ${inventory.data.window_lock.closed.map((ref) => ref.label).join(", ")}`
                  : ""}
                .
              </p>
            </>
          ) : null}
        </Tile>
      </div>

      <div className={styles.span4}>
        <Tile
          id="ops-unattended"
          title="Unattended work"
          sub="Anchoring and alerts, as EmitIQ reports them"
          of="The unattended-work check"
          reads={me?.role === "admin" ? [meRead, liveness] : [meRead]}
        >
          {me?.role !== "admin" ? (
            <p className={styles.empty} data-testid="dash-no-liveness">
              A company Admin sees whether the scheduled work has run. This is not a statement that it has.
            </p>
          ) : liveness.data ? (
            <>
              <StatusChip tone={liveness.data.needs_attention ? "warn" : "ok"}>
                {liveness.data.needs_attention ? "Needs attention" : "Running"}
              </StatusChip>
              <p className={styles.note}>
                Ledger anchor: {liveness.data.anchor.state.replace(/_/g, " ")}
                {liveness.data.anchor.unanchored_entries > 0
                  ? `, ${liveness.data.anchor.unanchored_entries} entries not yet anchored`
                  : ""}
                . {liveness.data.alerts.length} {liveness.data.alerts.length === 1 ? "alert" : "alerts"}.
              </p>
            </>
          ) : null}
        </Tile>
      </div>
    </div>
  );
}
